<template>
  <div id="tutorials">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="tutorial-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <v-icon
                class="tutorial-icon"
                :color="$isDarkTheme() ? '#CECECE' : '#383935'"
                >mdi-school</v-icon
              >
              TUTORIALS
            </h1>
          </div>
          <v-flex class="d-flex align-center mb-7 mt-12">
            <div v-if="checkAuthorization(['superAdmin'])" class="mr-6">
              <v-btn
                v-if="!this.openTutorialForm"
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold"
                @click="addNewHandler"
              >
                CREATE NEW TUTORIAL
              </v-btn>
              <div v-else class="close-link" @click="closebuilder">
                <v-icon>arrowleft</v-icon>
                <span class="close-link-text">{{
                  $t('common.builderClose')
                }}</span>
              </div>
            </div>
          </v-flex>
          <v-form
            v-model="isFormValid"
            v-if="openTutorialForm"
            class="mb-12"
            ref="addeditform"
          >
            <v-card class="py-10 px-15">
              <v-card-title class="px-0 font-weight-bold font-size-32">{{
                editingTutorial ? 'EDIT TUTORIAL' : 'TUTORIAL BUILDER'
              }}</v-card-title>
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >Tutorial Title</v-card-text
              >
              <v-text-field
                v-model="tutorialtitle"
                variant="outlined"
                placeholder="Enter a title"
                :rules="[rules.required, rules.length(3)]"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>

              <v-card-text
                class="pa-0 pt-2 mb-1 text-subtitle-1 font-weight-bold text-uppercase"
                >Tutorial SubTitle</v-card-text
              >
              <v-text-field
                v-model="tutorialsubtitle"
                variant="outlined"
                placeholder="Enter a subtitle"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>

              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >Tutorial URL</v-card-text
              >
              <v-text-field
                v-model="tutorialurl"
                variant="outlined"
                placeholder="Enter the URL"
                :rules="[rules.required]"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>
            </v-card>
            <div class="text-center">
              <v-btn
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold my-14 px-14"
                :disabled="!isFormValid"
                @click="editingTutorial ? editTutorial() : addTutorial()"
                >{{ editingTutorial ? 'EDIT TUTORIAL' : 'ADD TUTORIAL' }}</v-btn
              >
            </div>
            <v-progress-linear
              :indeterminate="iseditingTutorial"
              :active="iseditingTutorial"
              color="#592892"
            ></v-progress-linear>
            <v-alert
              v-if="feedback"
              border="start"
              color="#592892"
              class="text-center"
            >
              {{ feedback }}
            </v-alert>
            <v-divider class="dashed"></v-divider>
          </v-form>
          <v-data-table
            :headers="headers"
            :items="tutorials"
            :items-per-page="20"
            item-value="id"
            :class="[
              'v-data-table',
              $isDarkTheme() ? 'theme--dark' : 'theme--light',
            ]"
            class="elevation-1 churches-table"
            fixed-header
            :loading="isloading"
            loading-text="Loading Tutorials... Please wait"
            no-data-text="No Tutorials found"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, -1],
            }"
          >
            <template #headers>
              <tr>
                <th class="text-left"></th>
                <!-- grab -->
                <th class="text-left">Order</th>
                <th class="text-left">Title</th>
                <th class="text-left">SubTitle</th>
                <th class="text-left">Tutorial URL</th>
                <th class="text-left">Featured</th>
                <th class="text-right">Actions</th>
              </tr>
            </template>

            <template #item="{ item, index }">
              <tr
                draggable="true"
                @dragstart="onDragStart(index)"
                @dragover.prevent
                @drop="onDrop(index)"
                @dragenter.prevent="onDragEnter(index)"
                @dragleave="onDragLeave"
                :class="{ 'drop-target': dropIndex === index }"
              >
                <td><v-icon class="page__grab-icon">mdi-arrow-all</v-icon></td>
                <td>{{ index + 1 }}</td>
                <td>{{ item.tutorialtitle }}</td>
                <td>{{ item.tutorialsubtitle }}</td>
                <td>{{ item.tutorialurl }}</td>
                <td>
                  <div v-if="checkAuthorization(['superAdmin'])">
                    <v-tooltip
                      location="bottom"
                      :disabled="numFeaturedSections < 3"
                    >
                      <template #activator="{ props }">
                        <span v-bind="props">
                          <v-checkbox
                            color="#A5D867"
                            class="ma-0 pt-0 featured-switch"
                            false-icon="mdi-star"
                            true-icon="mdi-star"
                            v-model="item.featured"
                            hide-details
                            :disabled="
                              !checkAuthorization(['superAdmin']) ||
                              (numFeaturedSections >= 3 && !item.featured)
                            "
                            @update:model-value="setSectionToFeatured(item)"
                          />
                        </span>
                      </template>
                      <div class="text-center">
                        <p class="my-0">
                          You can only have 3 featured sections.
                        </p>
                      </div>
                    </v-tooltip>
                  </div>
                  <div v-else>
                    <v-icon :color="item.active ? '#A5D867' : '#E0E0E0'"
                      >mdi-star</v-icon
                    >
                  </div>
                </td>
                <td class="text-right">
                  <div class="action-icons d-flex align-center justify-end">
                    <v-icon
                      icon="edit"
                      class="mr-3"
                      @click="loadTutorialToEdit(item)"
                      :disabled="item.archive"
                    />
                    <v-tooltip v-if="!item.archive" location="bottom">
                      <template #activator="{ props }">
                        <span v-bind="props">
                          <v-icon
                            icon="logout"
                            class="archive-icon"
                            @click="removeTutorial(item)"
                          />
                        </span>
                      </template>
                      <span>Archive Tutorial</span>
                    </v-tooltip>
                    <v-tooltip v-else location="bottom">
                      <template #activator="{ props }">
                        <v-icon
                          v-bind="props"
                          icon="logout"
                          class="activate-icon"
                          @click="activateTutorial(item)"
                        />
                      </template>
                      <span>Activate Tutorial</span>
                    </v-tooltip>
                  </div>
                </td>
              </tr>
            </template>

            <template #item.order="{ index }">
              <td>{{ index + 1 }}</td>
            </template>

            <template #item.featured="{ item }">
              <td>
                <div v-if="checkAuthorization(['superAdmin'])">
                  <v-tooltip
                    location="bottom"
                    :disabled="numFeaturedSections < 3"
                  >
                    <template #activator="{ props }">
                      <span v-bind="props">
                        <v-checkbox
                          color="#A5D867"
                          class="ma-0 pt-0 featured-switch"
                          false-icon="mdi-star"
                          true-icon="mdi-star"
                          v-model="item.featured"
                          hide-details
                          :disabled="
                            !checkAuthorization(['superAdmin']) ||
                            (numFeaturedSections >= 3 && !item.featured)
                          "
                          @update:model-value="setSectionToFeatured(item)"
                        />
                      </span>
                    </template>
                    <div class="text-center">
                      <p class="my-0">You can only have 3 featured sections.</p>
                    </div>
                  </v-tooltip>
                </div>
                <div v-else>
                  <v-icon :color="item.active ? '#A5D867' : '#E0E0E0'"
                    >mdi-star</v-icon
                  >
                </div>
              </td>
            </template>

            <template #item.actions="{ item }">
              <td class="text-right d-flex justify-end">
                <div class="action-icons">
                  <v-icon
                    icon="edit"
                    class="mr-3"
                    @click="loadTutorialToEdit(item)"
                    :disabled="item.archive"
                  />

                  <v-tooltip
                    v-if="!item.archive && !isHelpDesk"
                    location="bottom"
                  >
                    <template #activator="{ props }">
                      <span v-bind="props">
                        <v-icon
                          icon="logout"
                          class="archive-icon"
                          @click="removeTutorial(item)"
                        />
                      </span>
                    </template>
                    <span>Archive Tutorial</span>
                  </v-tooltip>

                  <v-tooltip v-else location="bottom">
                    <template #activator="{ props }">
                      <v-icon
                        v-bind="props"
                        icon="logout"
                        class="activate-icon"
                        @click="activateTutorial(item)"
                      />
                    </template>
                    <span>Activate Tutorial</span>
                  </v-tooltip>
                </div>
              </td>
            </template>
          </v-data-table>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapGetters } from 'vuex'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'Tutorials',
    data() {
      return {
        feedback: '',
        tutorials: [],
        expanded: [],
        isloading: true,
        isFormValid: false,
        isediting: false,
        loader: null,
        tutorialtitle: '',
        tutorialsubtitle: '',
        tutorialurl: '',
        order: '',
        editingTutorial: false,
        openTutorialForm: false,
        iseditingTutorial: false,
        singleExpand: true,
        tutorialToEdit: null,
        draggingIndex: null,
        rules: {
          length: (len) => (v) =>
            (v || '').length >= len || `min ${len} characters required `,
          required: (v) => !!v || 'This field is required',
        },
        headers: [
          { text: '', value: 'grab', sortable: false }, // for the drag handle
          { text: 'Order', value: 'order', sortable: false },
          { text: 'Title', value: 'tutorialtitle', sortable: true },
          { text: 'SubTitle', value: 'tutorialsubtitle', sortable: false },
          { text: 'Tutorial URL', value: 'tutorialurl', sortable: false },
          { text: 'Featured', value: 'featured', sortable: false },
          {
            text: 'Actions',
            value: 'actions',
            sortable: false,
            align: 'right',
          },
        ],
      }
    },
    components: {
      HowToUseThisPage,
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      numFeaturedSections() {
        return this.tutorials.filter((value) => value.featured).length
      },
      isHelpDesk() {
        const roles = this.$store.getters['UserData/getRoles'] || []
        return roles.includes('helpDesk')
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler(event) {
          this.init(event)
        },
      },
      loader() {
        const l = this.loader
        this[l] = !this[l]
        this.loader = null
      },
    },
    methods: {
      addNewHandler() {
        this.tutorialtitle = ''
        this.tutorialsubtitle = ''
        this.tutorialurl = ''
        if (this.editingTutorial) {
          if (this.$refs.addeditform) this.$refs.addeditform.reset()
          this.openTutorialForm = true
        } else {
          this.openTutorialForm = !this.openTutorialForm
        }
        this.editingTutorial = false
      },
      async addTutorial() {
        this.editingTutorial = true

        if (this.tutorialtitle && this.tutorialurl) {
          db.collection('tutorials')
            .add({
              tutorialtitle: this.tutorialtitle,
              tutorialsubtitle: this.tutorialsubtitle,
              tutorialurl: this.tutorialurl,
              created: Date.now(),
            })
            .then(() => {
              // Reset fields
              this.tutorialtitle = ''
              this.tutorialsubtitle = ''
              this.tutorialurl = ''
              this.openTutorialForm = false
              this.editingTutorial = false

              // re-init the tutorials list
              this.init()
            })
        } else {
          this.feedback = 'A Title and URL are required'
        }
      },
      onDragStart(index) {
        this.draggingIndex = index
      },
      onDrop(dropIndex) {
        if (this.draggingIndex === null || dropIndex === this.draggingIndex)
          return
        const draggedItem = this.tutorials.splice(this.draggingIndex, 1)[0]
        this.tutorials.splice(dropIndex, 0, draggedItem)
        this.draggingIndex = null
        this.updateOrder(this.tutorials) // Already defined in your code
      },
      onDragEnter(index) {
        this.dropIndex = index
      },
      onDragLeave() {
        this.dropIndex = null
      },
      async removeTutorial(tutorial) {
        if (this.isHelpDesk) {
          const key = 'tutorials.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot modify tutorials.'
          return
        }

        const isDark = this.$isDarkTheme()

        if (!tutorial) {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the archive function, missing params.',
            icon: 'error',
            isDark,
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        const confirmed = await confirmAction({
          title: `You are about to remove: <br>"${tutorial.tutorialtitle}"`,
          html: 'Type DELETE below to confirm.',
          input: 'text',
          isDark,
          inputPlaceholder: 'Type DELETE',
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'DELETE')) {
                resolve()
              } else {
                resolve('You need to type DELETE to confirm')
              }
            })
          },
          confirmButtonText: 'Yes Remove it!',
          cancelButtonText: 'No, Keep it!',
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        try {
          await db.collection('tutorials').doc(tutorial.id).delete()
          this.init()
        } catch (error) {
          console.error('Error removing document:', error)
        }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const hasRole = permissions.filter(() =>
            userRoles.some((role) => permissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      closebuilder() {
        this.tutorialtitle = ''
        this.tutorialsubtitle = ''
        this.tutorialurl = ''
        this.openTutorialForm = false
        this.editingTutorial = false
      },
      async deleteItem(item, collection) {
        if (this.isHelpDesk) {
          const key = 'tutorials.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete tutorials.'
          return
        }

        if (!item || !collection) {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the revoke function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        const confirmed = await confirmAction({
          title: `You are about to remove tutorial: <br>"${item.key}"`,
          html: 'This change cannot be undone. If you are sure, type REMOVE in all caps below to confirm.',
          input: 'text',
          inputPlaceholder: 'REMOVE',
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'REMOVE')) {
                resolve()
              } else {
                resolve('You need to type REMOVE to confirm')
              }
            })
          },
          confirmButtonText: 'Yes, Remove it',
          cancelButtonText: 'No, Keep it',
          context: this,
        })

        if (!confirmed) return

        try {
          await db.collection(collection).doc(item.id).delete()
          this.init(this.$store.getters['AppData/cid'])
        } catch (error) {
          console.error('Error deleting document:', error)
        }
      },
      editTutorial() {
        this.iseditingTutorial = true

        if (this.tutorialtitle && this.tutorialurl) {
          setTimeout(() => {
            db.collection('tutorials')
              .doc(this.tutorialToEdit.id)
              .update({
                tutorialtitle: this.tutorialtitle,
                tutorialsubtitle: this.tutorialsubtitle,
                tutorialurl: this.tutorialurl,
              })
              .then(() => {
                // Reset fields
                this.tutorialtitle = ''
                this.tutorialsubtitle = ''
                this.tutorialurl = ''

                this.tutorialToEdit = null
                this.openTutorialForm = false
                this.editingTutorial = false

                // re-init the church list
                this.init()
              })
          }, 1000)
        } else {
          this.feedback = 'There was an error. '

          if (!this.tutorialtitle)
            this.feedback += 'You are missing a tutorial title. '
          if (!this.tutorialToEdit)
            this.feedback += 'There was no tutorial to edit. '
        }
      },
      loadTutorialToEdit(tutorial) {
        // console.log(tutorial)
        // check to see if this tutorial is already being edited and if so close the edit panel
        if (
          this.editingTutorial &&
          this.tutorialToEdit &&
          this.tutorialToEdit.title == tutorial.title
        ) {
          this.openTutorialForm = false
          this.editingTutorial = false
        } else {
          this.editingTutorial = true
          this.tutorialToEdit = tutorial
          this.openTutorialForm = true

          this.tutorialtitle = tutorial.tutorialtitle
          this.tutorialsubtitle = tutorial.tutorialsubtitle
          this.tutorialurl = tutorial.tutorialurl

          this.scrollToTop()
        }
      },
      row_classes(item) {
        let rowclass
        if (item.archive) rowclass = 'disabled'

        return rowclass
      },
      scrollToTop() {
        document
          .getElementById('tutorials')
          .scrollIntoView({ behavior: 'smooth' })
      },
      setSectionToFeatured(item) {
        db.collection('tutorials')
          .doc(item.id)
          .update({
            featured: item.featured,
          })
          .catch((err) => {
            console.log(err)
          })
      },
      updateOrder(items) {
        // console.log(items)
        for (let i = 0; i < items.length; i++) {
          const element = items[i]
          // console.log('el ', element)
          db.collection('tutorials')
            .doc(element.id)
            .update({
              order: i,
            })
            .catch((err) => {
              console.log(err)
            })
        }
      },
      init() {
        this.tutorials = []
        this.isloading = true

        // we set a timeout for the User experience
        setTimeout(() => {
          let tutorialsRef = db.collection('tutorials')
          // tutorialsRef = tutorialsRef.where('archive', '==', false)
          tutorialsRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                let tutorial = doc.data()
                tutorial.id = doc.id
                this.tutorials.push(tutorial)

                this.tutorials.sort((a, b) => (a.order < b.order ? -1 : 0))
              })
            })
            .then(() => {
              this.isloading = false
            })
        }, 1000)
      },
    },
    created() {
      this.init()
    },
  }
</script>

<style scoped>
  .tutorial-container {
    margin-top: 50px;
    margin-bottom: 25%;
  }
  .tutorial-container .tutorial-icon {
    margin-right: 10px;
    color: #000;
    width: 30px;
  }
  .show-response {
    max-height: 800px;
    overflow: scroll;
  }
  .show-sample-response {
    overflow-wrap: anywhere;
    padding: 30px;
  }
  .v-input.featured-switch .theme--light.v-icon {
    color: rgb(164 164 164 / 54%);
  }
  .page__grab-icon {
    cursor: grab;
  }
</style>
